import path from "node:path";
import { AssetManager } from "@skaile/workspaces/asset-manager";
import { Command } from "commander";
import { logErr, logOk, S } from "../helpers.ts";

/**
 * Creates the `skaile patch` command group.
 *
 * Manages the local patch workflow for improving deployed assets without
 * modifying the upstream repo directly. Subcommands: `extract` (copy asset
 * to `.skaile/patches/`), `commit` (generate a `.patch` file from edits),
 * `submit` (apply patch to the repo clone), `remove` (clean up after merge).
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/patch#make-patch-command
 */
export function makePatchCommand(): Command {
  const cmd = new Command("patch").description("Patch workflow for skill improvement");

  cmd
    .command("extract <ref>")
    .alias("start")
    .description("Extract asset for local editing")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (ref: string, opts) => {
      const am = new AssetManager({ projectDir: path.resolve(opts.projectDir) });
      try {
        const dest = await am.patch(ref);
        logOk(`Extracted to ${S.dim(dest)}`);
        console.log(`  Edit the files, then run ${S.cmd(`skaile patch commit ${ref}`)}`);
      } catch (err) {
        logErr(String(err));
        process.exit(1);
      }
    });

  cmd
    .command("commit <ref>")
    .description("Generate .patch file from edits")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (ref: string, opts) => {
      const am = new AssetManager({ projectDir: path.resolve(opts.projectDir) });
      try {
        const patchFile = await am.patchCommit(ref);
        logOk(`Patch saved to ${S.dim(patchFile)}`);
        console.log(`  Add to skaile.yaml patches section, then run ${S.cmd("skaile install")}`);
      } catch (err) {
        logErr(String(err));
        process.exit(1);
      }
    });

  cmd
    .command("submit <ref>")
    .description("Apply patch to repo clone and prepare for PR")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (ref: string, opts) => {
      const am = new AssetManager({ projectDir: path.resolve(opts.projectDir) });
      try {
        await am.patchSubmit(ref);
        logOk("Patch applied to repo clone and committed.");
        console.log(
          `  Push upstream from the source repo directly — the legacy ${S.cmd("skaile repo contrib push")} command was removed on 2026-05-12.`,
        );
      } catch (err) {
        logErr(String(err));
        process.exit(1);
      }
    });

  cmd
    .command("remove <ref>")
    .description("Remove local patch after upstream merge")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action((ref: string, opts) => {
      const am = new AssetManager({ projectDir: path.resolve(opts.projectDir) });
      am.patchRemove(ref);
      logOk(`Patch removed for ${ref}`);
    });

  cmd.action(() => cmd.help());
  return cmd;
}
